import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, startOfMonth, endOfMonth, getWeek, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import Modal from 'react-modal';
import { getDevOpsMembers, getTeamEvents, createTeamEvent, updateTeamEvent, deleteTeamEvent, sendGuardiasReport, getGuardiaContacts } from './api';
import './Guardias.css';

function Guardias() {
  const [guardias, setGuardias] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [reportModalIsOpen, setReportModalIsOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendingReport, setSendingReport] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [formData, setFormData] = useState({
    member_email: '',
    event_type: 'N1',
    week_start: '',
    penalized: false
  });

  const loadGuardias = useCallback(async () => {
    try {
      // Obtener todas las guardias (N1 y N2)
      const response = await getTeamEvents();
      const guardiasData = response.data.filter(
        event => event.event_type === 'N1' || event.event_type === 'N2'
      );
      // Ordenar por fecha de inicio
      guardiasData.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
      setGuardias(guardiasData);
    } catch (error) {
      console.error('Error loading guardias:', error);
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
    loadTeamMembers();
    loadGuardias();
    loadContacts();
  }, [loadGuardias]);

  const loadContacts = async () => {
    try {
      const response = await getGuardiaContacts();
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading guardia contacts:', error);
    }
  };

  const loadCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await fetch('/api/auth/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const user = await response.json();
        setCurrentUser(user);
      }
    } catch (error) {
      console.error('Error loading user:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await getDevOpsMembers();
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const openModal = (weekStart = null, eventType = 'N1', existingGuardia = null) => {
    setSelectedWeek(weekStart);

    if (weekStart) {
      setFormData({
        member_email: existingGuardia ? existingGuardia.member_email : '',
        event_type: eventType,
        week_start: format(weekStart, 'yyyy-MM-dd'),
        guardia_id: existingGuardia ? existingGuardia.id : null,
        penalized: existingGuardia ? existingGuardia.penalized || false : false
      });
    } else {
      setFormData({
        member_email: '',
        event_type: 'N1',
        week_start: '',
        guardia_id: null,
        penalized: false
      });
    }

    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedWeek(null);
    setFormData({
      member_email: '',
      event_type: 'N1',
      week_start: '',
      guardia_id: null,
      penalized: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        member_email: formData.member_email,
        event_type: formData.event_type,
        start_date: formData.week_start,
        end_date: format(endOfWeek(parseISO(formData.week_start), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
        penalized: formData.penalized
      };

      if (formData.guardia_id) {
        await updateTeamEvent(formData.guardia_id, eventData);
      } else {
        await createTeamEvent(eventData);
      }

      loadGuardias();
      closeModal();
    } catch (error) {
      console.error('Error saving guardia:', error);
      alert('Error al guardar la guardia');
    }
  };

  const handleDelete = async () => {
    if (formData.guardia_id && window.confirm('¿Estás seguro de eliminar esta guardia?')) {
      try {
        await deleteTeamEvent(formData.guardia_id);
        loadGuardias();
        closeModal();
      } catch (error) {
        console.error('Error deleting guardia:', error);
        alert('Error al eliminar la guardia');
      }
    }
  };

  const getMemberName = (email) => {
    const member = teamMembers.find(m => m.email === email);
    return member ? member.name : email;
  };

  // Generar todas las semanas del mes
  const generateWeeksOfMonth = () => {
    const weeks = [];
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);

    // Empezar desde el lunes de la semana que contiene el primer día del mes
    let currentWeekStart = startOfWeek(monthStart, { weekStartsOn: 1 });

    // Continuar hasta cubrir todo el mes
    while (currentWeekStart <= monthEnd) {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const weekNumber = getWeek(currentWeekStart, { weekStartsOn: 1, firstWeekContainsDate: 4 });

      weeks.push({
        weekNumber,
        start: currentWeekStart,
        end: weekEnd
      });

      currentWeekStart = addWeeks(currentWeekStart, 1);
    }

    return weeks;
  };

  // Obtener guardia asignada para una semana y tipo
  const getGuardiaForWeek = (weekStart, eventType) => {
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    return guardias.find(g => {
      const guardiaStart = format(startOfWeek(parseISO(g.start_date), { weekStartsOn: 1 }), 'yyyy-MM-dd');
      return guardiaStart === weekStartStr && g.event_type === eventType;
    });
  };

  const weeks = generateWeeksOfMonth();
  const isAdmin = currentUser?.role === 'admin';

  const previousMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const goToToday = () => setCurrentMonth(new Date());

  const openReportModal = () => {
    if (!isAdmin) {
      alert('Solo los administradores pueden enviar reportes');
      return;
    }
    setSelectedRecipients([]);
    setReportModalIsOpen(true);
  };

  const closeReportModal = () => {
    setReportModalIsOpen(false);
    setSelectedRecipients([]);
  };

  const toggleRecipient = (email) => {
    if (selectedRecipients.includes(email)) {
      setSelectedRecipients(selectedRecipients.filter(e => e !== email));
    } else {
      setSelectedRecipients([...selectedRecipients, email]);
    }
  };

  const handleSendReport = async () => {
    if (selectedRecipients.length === 0) {
      alert('Selecciona al menos un destinatario');
      return;
    }

    try {
      setSendingReport(true);
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();

      await sendGuardiasReport(month, year, selectedRecipients);

      alert('Reporte enviado exitosamente');
      closeReportModal();
    } catch (error) {
      console.error('Error sending report:', error);
      alert('Error al enviar el reporte: ' + (error.response?.data?.error || error.message));
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <div className="guardias-container">
      <div className="guardias-section">
        <div className="section-header">
          <h2>Guardias</h2>
          <div className="month-navigation">
            <button onClick={previousMonth} className="nav-button">← Anterior</button>
            <div className="month-display">
              <h3>{format(currentMonth, 'MMMM yyyy', { locale: es })}</h3>
              <button onClick={goToToday} className="today-button">Hoy</button>
            </div>
            <button onClick={nextMonth} className="nav-button">Siguiente →</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={openReportModal}
              className="btn-report"
              disabled={!isAdmin}
              title={!isAdmin ? 'Solo administradores pueden enviar reportes' : 'Enviar reporte de guardias por email'}
            >
              📧 Enviar Reporte
            </button>
            {!isAdmin && <span className="admin-only-badge">Solo Admin</span>}
          </div>
        </div>

        <div className="guardias-table-container">
          <table className="guardias-table">
            <thead>
              <tr>
                <th>Nº Semana</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Asignado N1</th>
                <th>Asignado N2</th>
              </tr>
            </thead>
            <tbody>
              {weeks.map(week => {
                const n1Guardia = getGuardiaForWeek(week.start, 'N1');
                const n2Guardia = getGuardiaForWeek(week.start, 'N2');

                return (
                  <tr key={week.weekNumber}>
                    <td style={{ textAlign: 'center', fontWeight: 'bold' }}>{week.weekNumber}</td>
                    <td>{format(week.start, 'dd/MM/yyyy')}</td>
                    <td>{format(week.end, 'dd/MM/yyyy')}</td>
                    <td
                      className="assignable-cell"
                      onClick={() => openModal(week.start, 'N1', n1Guardia)}
                      style={{ cursor: 'pointer' }}
                    >
                      {n1Guardia ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                          <span className="badge badge-n1">{getMemberName(n1Guardia.member_email)}</span>
                          {n1Guardia.penalized && (
                            <span title="Guardia penalizada - No se pagará" style={{ fontSize: '1.2rem' }}>⛔</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>Click para asignar</span>
                      )}
                    </td>
                    <td
                      className="assignable-cell"
                      onClick={() => openModal(week.start, 'N2', n2Guardia)}
                      style={{ cursor: 'pointer' }}
                    >
                      {n2Guardia ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                          <span className="badge badge-n2">{getMemberName(n2Guardia.member_email)}</span>
                          {n2Guardia.penalized && (
                            <span title="Guardia penalizada - No se pagará" style={{ fontSize: '1.2rem' }}>⛔</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>Click para asignar</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="contacts-section">
        <div className="team-contacts">
          <h3>Teléfonos del Equipo</h3>
          <div className="contacts-grid">
            {contacts.filter(contact => contact.category === 'equipo').map(contact => (
              <div key={contact.id} className="contact-card">
                <div className="contact-name">{contact.name}</div>
                <div className="contact-phone">{contact.phone}</div>
                <div className="contact-extension">
                  {contact.extension ? `Extensión Zadarma: ${contact.extension}` : 'Sin extensión Zadarma'}
                </div>
              </div>
            ))}
          </div>
          {currentUser?.role === 'admin' && (
            <p className="contacts-hint">Los contactos se editan en ⚙️ Configuración → Contactos de guardia</p>
          )}
        </div>

        <div className="special-contacts">
          <h3>Contactos Especiales</h3>
          <div className="contacts-grid">
            {contacts.filter(contact => contact.category === 'especial').map(contact => (
              <div key={contact.id} className="contact-card special">
                <div className="contact-name">{contact.name}</div>
                <div className="contact-phone">{contact.phone}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="Guardia Modal"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h2>{formData.guardia_id ? 'Editar' : 'Asignar'} Guardia {formData.event_type}</h2>
        {selectedWeek && (
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            <strong>Semana:</strong> {format(selectedWeek, 'dd/MM/yyyy')} - {format(endOfWeek(selectedWeek, { weekStartsOn: 1 }), 'dd/MM/yyyy')}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Usuario *</label>
            <select
              value={formData.member_email}
              onChange={(e) => setFormData({ ...formData, member_email: e.target.value })}
              required
            >
              <option value="">Seleccionar usuario</option>
              {teamMembers.map(member => (
                <option key={member.email} value={member.email}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>

          {isAdmin && (formData.event_type === 'N1' || formData.event_type === 'N2') && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.penalized}
                  onChange={(e) => setFormData({ ...formData, penalized: e.target.checked })}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span>⛔ Guardia Penalizada (no se pagará)</span>
              </label>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                Esta semana de guardia no se pagará al empleado y aparecerá marcada en los reportes.
              </p>
            </div>
          )}

          <div className="modal-buttons">
            <button type="submit" className="btn-primary">
              {formData.guardia_id ? 'Actualizar' : 'Asignar'}
            </button>
            {formData.guardia_id && (
              <button type="button" onClick={handleDelete} className="btn-danger">
                Eliminar
              </button>
            )}
            <button type="button" onClick={closeModal} className="btn-secondary">
              Cancelar
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={reportModalIsOpen}
        onRequestClose={closeReportModal}
        contentLabel="Enviar Reporte"
        className="modal"
        overlayClassName="modal-overlay"
      >
        <h2>Enviar Reporte de Guardias</h2>
        <p style={{ color: '#666', marginBottom: '1rem' }}>
          Mes seleccionado: <strong>{format(currentMonth, 'MMMM yyyy', { locale: es })}</strong>
        </p>

        <div className="form-group">
          <label>Selecciona los destinatarios:</label>
          <div className="recipients-list">
            {teamMembers.map(member => (
              <label key={member.email} className="recipient-item">
                <input
                  type="checkbox"
                  checked={selectedRecipients.includes(member.email)}
                  onChange={() => toggleRecipient(member.email)}
                />
                <span>{member.name}</span>
                <span className="recipient-email">{member.email}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="report-preview">
          <p><strong>Destinatarios seleccionados:</strong> {selectedRecipients.length}</p>
        </div>

        <div className="modal-buttons">
          <button
            type="button"
            onClick={handleSendReport}
            className="btn-primary"
            disabled={sendingReport || selectedRecipients.length === 0}
          >
            {sendingReport ? 'Enviando...' : 'Enviar Reporte'}
          </button>
          <button
            type="button"
            onClick={closeReportModal}
            className="btn-secondary"
            disabled={sendingReport}
          >
            Cancelar
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default Guardias;
